import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Combobox from '../Combobox';

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;
const MIN_SCAN_CHARS = 4;

/**
 * The camp-data "Athlete" box: name typeahead AND barcode scans.
 *
 * A scanner types the athlete's uid and presses Enter, so Enter with no row
 * highlighted is a scan (`onScan`), while Enter on a highlighted row picks it.
 * The page owns the text and what a pick or a scan means; this box owns the
 * search, the stale-response guard and the dropdown, on the shared Combobox.
 *
 * Behaviour that used to be missing:
 *  - a slow earlier response could overwrite a newer one (no guard);
 *  - a failed request looked like "no matches";
 *  - a scan left a name search pending, so a dropdown could flash over the
 *    athlete the scan had just selected.
 */
export default function AthleteLookupBox({
  apiUrl,
  value,
  onValueChange,   // (text) => void   — typing
  onSelect,        // (athlete) => void — an explicit pick
  onScan,          // (text) => void   — Enter with nothing highlighted
  athleteSelected, // an athlete is already chosen: Enter is not a scan
  inputRef,
  placeholder = 'Type name or scan code...',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | none | error
  const timer = useRef(null);
  const seq = useRef(0);
  const lastTyped = useRef(value);

  // Invalidate any scheduled or in-flight search. These touch only refs and state
  // setters, so they are stable and can be effect dependencies without a disable
  // comment (CRA's production build rejects inline react-hooks disables).
  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    seq.current += 1;
  }, []);
  const clear = useCallback(() => {
    cancel();
    setSuggestions([]);
    setStatus('idle');
  }, [cancel]);

  // The page changed the text itself (a pick, a camera scan, a reset): whatever
  // this box was suggesting no longer applies.
  useEffect(() => {
    if (value !== lastTyped.current) {
      lastTyped.current = value;
      clear();
    }
  }, [value, clear]);

  useEffect(() => () => { clearTimeout(timer.current); seq.current += 1; }, []);

  const run = async (text) => {
    seq.current += 1;
    const mine = seq.current;
    try {
      const res = await axios.get(`${apiUrl}/athletes/lookup-by-name`, { params: { q: text } });
      if (mine !== seq.current) return;           // a newer search or a clear superseded this one
      setSuggestions(res.data);
      setStatus(res.data.length === 0 ? 'none' : 'idle');
    } catch {
      if (mine !== seq.current) return;
      setSuggestions([]);
      setStatus('error');
    }
  };

  const handleInput = (text) => {
    lastTyped.current = text;
    onValueChange(text);
    cancel();
    if (text.trim().length < MIN_QUERY) {
      setSuggestions([]);
      setStatus('idle');
      return;
    }
    timer.current = setTimeout(() => run(text), DEBOUNCE_MS);
  };

  const handleKeyDown = (e) => {
    // Reached only when the combobox did not use the key, i.e. no row is highlighted.
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const text = (value || '').trim();
    clear();                                       // no dropdown over the scanned athlete
    if (text.length >= MIN_SCAN_CHARS && !athleteSelected) onScan(text);
  };

  return (
    <>
      <Combobox
        value={value}
        onInputChange={handleInput}
        items={suggestions}
        getKey={(a) => a.user_id}
        renderItem={(a) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {a.profile_photo ? (
              <img src={a.profile_photo} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: '#e2e8f0', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600, color: '#6b7280',
              }}>
                {a.first_name?.[0]}{a.last_name?.[0]}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{a.first_name} {a.last_name}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{(a.role || '').replace('_', ' ')}</div>
            </div>
          </div>
        )}
        onSelect={(a) => { clear(); onSelect(a); }}
        placeholder={placeholder}
        ariaLabel="Athlete"
        inputProps={{ ref: inputRef, onKeyDown: handleKeyDown }}
        classNames={{
          input: 'form-field__input',
          popup: 'school-picker__results',
          option: 'school-picker__result',
          optionActive: 'school-picker__result--active',
        }}
      />
      {status === 'none' && (
        <p style={{ color: '#ef4444', fontSize: 13, marginTop: 4 }}>No matches found</p>
      )}
      {status === 'error' && (
        <p style={{ color: '#ef4444', fontSize: 13, marginTop: 4 }}>
          Search is unavailable right now — try again.
        </p>
      )}
    </>
  );
}
